import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { PointOfSale } from '@/api/admin-sales-channels-types'
import { useUpdatePointOfSale } from '@/api/api-mutations'
import {
	preloadListPointOfSaleGroups,
	preloadListSalesChannels,
	useListPointOfSaleGroups,
	useListSalesChannels
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker } from '@/components/route-modal'
import { useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/$pointOfSaleUuid/edit')({
	component: PointOfSaleEdit,
	loader: async ({ context }) => {
		await preloadListSalesChannels(context, {})
		await preloadListPointOfSaleGroups(context, {})
	}
})

function PointOfSaleEdit() {
	const { pointOfSaleUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => await api.getPointOfSale({ path: { pointOfSaleUuid } })}
		>
			{data => <PointOfSaleEditComponent pointOfSale={data} />}
		</RouteModal>
	)
}

function PointOfSaleEditComponent({ pointOfSale }: { pointOfSale: PointOfSale }) {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()

	const updatePointOfSale = useUpdatePointOfSale()
	const salesChannels = useListSalesChannels()

	const salesChannelOptions = useMemo(
		() =>
			salesChannels.data?.map(channel => ({
				label: channel.name,
				value: channel.uuid
			})) || [],
		[salesChannels.data]
	)

	const pointOfSaleGroups = useListPointOfSaleGroups()
	const groupOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.PointOfSale.GroupNone() },
			...(pointOfSaleGroups.data ?? []).map(group => ({ value: group.uuid, label: group.name }))
		],
		[L, pointOfSaleGroups.data]
	)

	const form = useAppForm({
		defaultValues: {
			name: pointOfSale.name,
			identifier: pointOfSale.identifier,
			salesChannelUuids: pointOfSale.salesChannels.map(sc => sc.uuid),
			pointOfSaleGroupUuid: pointOfSale.group?.uuid ?? ''
		},
		onSubmit: async ({ value }) => {
			const result = await updatePointOfSale({
				path: { pointOfSaleUuid },
				body: {
					name: value.name,
					identifier: value.identifier,
					salesChannelUuids: value.salesChannelUuids,
					pointOfSaleGroupUuid: value.pointOfSaleGroupUuid
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.PointOfSale.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField name='name' children={field => <field.TextField label={L.Fields.Name()} />} />
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='identifier'
							children={field => <field.TextField label={L.Settings.PointOfSale.Column.Identifier()} />}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='salesChannelUuids'
							children={field => (
								<field.ComboboxField
									label={L.Fields.SalesChannel()}
									placeholder='Select sales channels'
									multiple
									options={salesChannelOptions}
									optionsLoading={salesChannels.isLoading}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='pointOfSaleGroupUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.PointOfSale.Column.Group()}
									options={groupOptions}
									optionsLoading={pointOfSaleGroups.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.PointOfSale.GroupOptionalHint()}
						</Text>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
